"use client"

import { fetchPostByCategories } from "@/(admin)/modules/post/services/post"
import PostCard from "@/components/post-card"
import { PostData, TagData } from "@/lib/config/types"
import { ComponentConfig } from "@measured/puck"
import { useEffect, useState } from "react"
import "swiper/css"
import "swiper/css/pagination"
import { twMerge } from "tailwind-merge"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
export interface ArticleItem {
  image1?: string
  image2?: string
  time?: string
  itemTitle?: string
  excerpt?: string
}

export interface ArticlesProps extends CommonStylesProps {
  type: "fluid" | "custom" | "boxed"
  gapCustom?: string
  heightContact?: string
  category?: string[]
  articleItem: ArticleItem[]
}

export const RenderConfig: ComponentConfig<ArticlesProps> = {
  render: ({ className, category, styles, gapCustom, responsiveType, articleItem }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    const [articleItems, setArticleItems] = useState<PostData[]>([])
    const [filteredArticles, setFilteredArticles] = useState<PostData[]>([])
    const [tags, setTags] = useState<string[]>([])
    const [activeTag, setActiveTag] = useState<string>("All")
    const lang = typeof window !== "undefined" ? document.documentElement.lang || "en" : "en"

    const fetchPostByCategoryId = async (category: string) => {
      const responsePostByCategory = await fetchPostByCategories([category], lang || "en")
      const posts = responsePostByCategory?.data || []

      setArticleItems(posts)
      setFilteredArticles(posts)

      const allTags = posts.flatMap((post: PostData) =>
        (post.attributes.tags?.data || []).map((tag: TagData) => tag.attributes?.name || ""),
      )
      const uniqueTags: string[] = [...new Set(allTags)].filter(
        (tag): tag is string => typeof tag === "string" && tag.length > 0,
      )

      setTags(uniqueTags)
    }

    useEffect(() => {
      fetchPostByCategoryId(String(category))
    }, [category])

    useEffect(() => {
      if (activeTag === "All") {
        setFilteredArticles(articleItems)
      } else {
        const filtered = articleItems.filter(post =>
          post.attributes.tags?.data?.some(tag => tag.attributes.name === activeTag),
        )
        setFilteredArticles(filtered)
      }
    }, [activeTag, articleItems])

    return (
      <>
        {responsiveCSS}
        <div id={id} className={twMerge(className, "w-full flex flex-col gap-10")}>
          {/* Tag list with horizontal scroll on mobile */}
          <div className="flex flex-row gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              className={twMerge(
                "py-1.5 px-2.5 rounded-lg text-center whitespace-nowrap flex-shrink-0 transition-all duration-300",
                activeTag === "All" ? "bg-[#EBEDF0] text-[#242F3E] font-bold" : "bg-[#FFFFFF] hover:bg-[#F5F5F5]",
              )}
              onClick={() => setActiveTag("All")}
            >
              All
            </button>
            {tags.map((tag, index) => (
              <button
                key={index}
                className={twMerge(
                  "py-1.5 px-2.5 rounded-lg text-center whitespace-nowrap flex-shrink-0 transition-all duration-300 bg-transparent hover:bg-[#F5F5F5]",
                  activeTag === tag ? "bg-[#EBEDF0] text-[#242F3E] font-bold" : "",
                )}
                onClick={() => setActiveTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mx-auto">
            {filteredArticles.map((post, index) => (
              <PostCard key={post.id} post={post} locale={lang} />
            ))}
          </div>
        </div>
        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </>
    )
  },
}
